import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Alert, Spinner, Modal, Tab, Nav } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { clientService } from '@/services/clientService';
import { clientMetadataService } from '@/services/clientMetadataService';
import { type ClientConfig, clientConfigSchema, CLIENT_STATUS_VALUES, CLIENT_STATUS_DISPLAY } from '@/types';
import type { ClientStatusValue } from '@/types';
import { ClientConfigForm } from '@/components/config/ClientConfigForm';
import { QuotaCheckCard } from '@/components/tools/QuotaCheckCard';
import { validateClientName, sanitizeClientName } from '@/utils/clientValidation';

export default function CreateClientConfig() {
  const [searchParams] = useSearchParams();

  const [clientName, setClientName] = useState('');
  const [clientNameError, setClientNameError] = useState<string | null>(null);
  const [clientAccountId, setClientAccountId] = useState('');
  // Mirrors the form's Region field so the Quota Check tab uses the live value
  const [region, setRegion] = useState('us-east-1');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<ClientConfig | null>(null);

  // Client metadata (non-deployment)
  const [metaStatus, setMetaStatus] = useState<ClientStatusValue>('unclear');
  const [trialStartDate, setTrialStartDate] = useState('');
  const [trialEndDate, setTrialEndDate] = useState('');
  const [metaNotes, setMetaNotes] = useState('');

  // Developer JSON create
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [uploadedConfig, setUploadedConfig] = useState<ClientConfig | null>(null);
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  // Prefill from query params when available
  useEffect(() => {
    const qClient = (searchParams.get('clientName') || '').trim();
    const qAccount = (searchParams.get('accountId') || '').trim();
    if (qClient && !clientName) setClientName(qClient);
    if (qAccount && !clientAccountId) setClientAccountId(qAccount);
    // only run once on mount
  }, []);

  // Validate client name whenever it changes
  useEffect(() => {
    if (clientName.trim().length > 0) {
      const error = validateClientName(clientName);
      setClientNameError(error);
    } else {
      setClientNameError(null);
    }
  }, [clientName]);

  const handleFormSubmit = (minimal: Partial<ClientConfig>) => {
    setError(null);
    setSuccess(null);
    if (!clientName || !clientAccountId) {
      setError('Client name and account ID are required');
      return;
    }
    if (clientNameError) {
      setError(`Invalid client name: ${clientNameError}`);
      return;
    }
    setPendingConfig({ ...minimal, clientAccountId } as ClientConfig);
    setShowPreview(true);
  };

  return (
    <div>
      {/* Client Identity - always visible above tabs */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header>
          <h5 className="mb-0">Create Client Config</h5>
          <p className="text-muted small mb-0 mt-2">
            Create a new client configuration. All fields show defaults and are ready to deploy immediately. For
            advanced features like data sources or custom domains, contact a developer.
          </p>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Client Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="e.g. arcanum-demo"
                  className={clientNameError ? 'border-danger' : clientName ? 'border-success' : ''}
                  isInvalid={!!clientNameError}
                />
                {clientNameError ? (
                  <>
                    <Form.Control.Feedback type="invalid">{clientNameError}</Form.Control.Feedback>
                    {sanitizeClientName(clientName) && sanitizeClientName(clientName) !== clientName && (
                      <Form.Text className="text-info">
                        Suggested: <strong>{sanitizeClientName(clientName)}</strong>
                      </Form.Text>
                    )}
                  </>
                ) : (
                  <Form.Text className="text-muted">Unique identifier for this client deployment</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Account ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={clientAccountId}
                  onChange={(e) => setClientAccountId(e.target.value)}
                  placeholder="12-digit AWS account ID"
                  className={clientAccountId ? 'border-primary' : ''}
                />
                <Form.Text className="text-muted">AWS account where resources will be deployed</Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tab.Container defaultActiveKey="config">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="config">Deployment Configuration</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="metadata">Client Metadata</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="quotas">Quota Check</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="json">JSON (Developers)</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Tab 1: Deployment Configuration */}
          <Tab.Pane eventKey="config">
            <ClientConfigForm
              mode="create"
              working={submitting}
              submitLabel="Create Configuration"
              submitDisabled={!!clientNameError}
              submitHint={
                clientNameError ? 'Fix validation errors to continue' : 'Defaults apply for unspecified settings'
              }
              onSubmit={handleFormSubmit}
              onError={setError}
              onRegionChange={setRegion}
            />
          </Tab.Pane>

          {/* Tab 2: Client Metadata */}
          <Tab.Pane eventKey="metadata">
            <Card className="border-0 shadow-sm">
              <Card.Header>
                <h6 className="mb-0">Client Metadata (Non-Deployment)</h6>
                <p className="text-muted small mb-0 mt-2">
                  Optional. Track trial status, dates, and notes. This data is stored separately from the deployment
                  config and does not affect infrastructure.
                </p>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Status</Form.Label>
                      <Form.Select
                        value={metaStatus}
                        onChange={(e) => setMetaStatus(e.target.value as ClientStatusValue)}
                      >
                        {CLIENT_STATUS_VALUES.map((s) => (
                          <option key={s} value={s}>
                            {CLIENT_STATUS_DISPLAY[s].label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className={metaStatus !== 'trial' ? 'text-muted' : ''}>Trial Start Date</Form.Label>
                      <DatePicker
                        selected={trialStartDate ? new Date(trialStartDate) : null}
                        onChange={(date: Date | null) =>
                          setTrialStartDate(date ? date.toISOString().split('T')[0] : '')
                        }
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select start date"
                        disabled={metaStatus !== 'trial'}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className={metaStatus !== 'trial' ? 'text-muted' : ''}>Trial End Date</Form.Label>
                      <DatePicker
                        selected={trialEndDate ? new Date(trialEndDate) : null}
                        onChange={(date: Date | null) => setTrialEndDate(date ? date.toISOString().split('T')[0] : '')}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select end date"
                        disabled={metaStatus !== 'trial'}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        minDate={trialStartDate ? new Date(trialStartDate) : undefined}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={metaNotes}
                    onChange={(e) => setMetaNotes(e.target.value)}
                    placeholder="Free text notes about this client..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab 3: Quota Check */}
          <Tab.Pane eventKey="quotas">
            {clientAccountId && region ? (
              <QuotaCheckCard accountId={clientAccountId} region={region} disabled={submitting} />
            ) : (
              <Alert variant="info">Enter an Account ID and select a Region above to check Bedrock quotas.</Alert>
            )}
          </Tab.Pane>

          {/* Tab 4: Developer JSON Create */}
          <Tab.Pane eventKey="json">
            {/* Developer JSON Create */}
            <Card className="border-0 shadow-sm mt-4">
              <Card.Header>
                <h6 className="mb-0">Create from JSON (Developers)</h6>
                <p className="text-muted small mb-0 mt-2">
                  Upload a full JSON config and create using the Client Name above. Validation is strict and mirrors
                  replace semantics in the CLI.
                </p>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-end g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Upload JSON file</Form.Label>
                      <Form.Control
                        type="file"
                        accept="application/json,.json"
                        onChange={async (e) => {
                          setJsonError(null);
                          setUploadedConfig(null);
                          const file = e.currentTarget.files?.[0];
                          if (!file) return;
                          try {
                            const text = await file.text();
                            const parsed = JSON.parse(text);
                            const validated = clientConfigSchema.strict().parse(parsed);
                            setUploadedConfig(validated as ClientConfig);
                          } catch (err: any) {
                            setJsonError(err?.message || 'Invalid JSON or schema mismatch');
                          }
                        }}
                      />
                      <Form.Text className="text-muted">
                        Provide a complete JSON config. Unknown fields are not allowed.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      disabled={!uploadedConfig || !clientName || !!clientNameError}
                      onClick={() => setShowJsonPreview(true)}
                    >
                      Preview JSON
                    </Button>
                    <Button
                      variant="primary"
                      disabled={!uploadedConfig || !clientName || !!clientNameError || submitting}
                      onClick={async () => {
                        if (!uploadedConfig || !clientName || clientNameError) return;
                        setError(null);
                        try {
                          setSubmitting(true);
                          await clientService.createClientConfig(clientName, uploadedConfig);
                          setSuccess('Client configuration created successfully');
                          setShowJsonPreview(false);
                          setUploadedConfig(null);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to create client config');
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      Create From JSON
                    </Button>
                  </Col>
                </Row>
                {jsonError && (
                  <Alert variant="danger" className="mt-3">
                    {jsonError}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Create</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Please review the configuration to be created for <strong>{clientName || '—'}</strong>:
          </p>
          <pre className="bg-light p-3 rounded" style={{ maxHeight: '50vh', overflow: 'auto' }}>
            {pendingConfig
              ? JSON.stringify(
                  pendingConfig,
                  Object.keys(pendingConfig as Record<string, unknown>)
                    .filter((k) => (pendingConfig as Record<string, unknown>)[k] !== undefined)
                    .sort(),
                  2
                )
              : ''}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={submitting}
            onClick={async () => {
              if (!pendingConfig) return;
              setError(null);
              try {
                setSubmitting(true);
                await clientService.createClientConfig(clientName, pendingConfig);
                if (metaStatus !== 'unclear' || trialStartDate || trialEndDate || metaNotes) {
                  try {
                    await clientMetadataService.saveMetadata({
                      clientName,
                      status: metaStatus,
                      ...(trialStartDate ? { trialStartDate } : {}),
                      ...(trialEndDate ? { trialEndDate } : {}),
                      ...(metaNotes ? { notes: metaNotes } : {}),
                    });
                  } catch (metaErr) {
                    console.error('Failed to save metadata:', metaErr);
                  }
                }
                setSuccess('Client configuration created successfully');
                setShowPreview(false);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to create client config');
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating…
              </>
            ) : (
              'Confirm Create'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showJsonPreview} onHide={() => setShowJsonPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>JSON Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Creating configuration for <strong>{clientName || '—'}</strong> with uploaded JSON:
          </p>
          <pre className="bg-light p-3 rounded" style={{ maxHeight: '50vh', overflow: 'auto' }}>
            {uploadedConfig ? JSON.stringify(uploadedConfig, null, 2) : ''}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJsonPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
